import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ExternalLink, ArrowLeft } from 'lucide-react';
import { Btn, LoadingSpinner } from '@/presentation/component/ui';
import { useAuth } from '@/presentation/hook/useAuth';
import { usePayment } from '@/presentation/hook/usePayment';

type Status = 'confirming' | 'success' | 'error';

export const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addPurchasedSubcategory, checkAuth } = useAuth();
  const { confirmPayment } = usePayment();

  const [status, setStatus] = useState<Status>('confirming');
  const [details, setDetails] = useState<{
    transactionId: string;
    amount: string;
    currency: string;
  } | null>(null);

  const clientSecret = searchParams.get('payment_intent_client_secret');
  const subcategoryName = searchParams.get('subcategory');
  const subcategoryId = searchParams.get('subcategory_id');

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const handleCompletion = async () => {
      if (!clientSecret) {
        setStatus('error');
        return;
      }

      try {
        const paymentIntentId = clientSecret.split('_secret_')[0];
        await confirmPayment(paymentIntentId);
      } catch {
        // confirmation may fail if already processed by webhook
      }

      if (subcategoryId) {
        addPurchasedSubcategory(subcategoryId);
        await checkAuth();
      }

      setDetails({
        transactionId: clientSecret,
        amount: clientSecret.includes('demo') ? 'Demo' : 'N/A',
        currency: 'EUR',
      });
      setStatus('success');
    };

    handleCompletion();
  }, [clientSecret, subcategoryId, addPurchasedSubcategory, checkAuth, confirmPayment]);

  if (status === 'confirming') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-[var(--color-surface)] rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <LoadingSpinner />
          <h1 className="text-2xl font-semibold text-[var(--color-ink)] mb-4 mt-4">
            {t('payment.confirmingPayment')}
          </h1>
          <p className="text-[var(--color-ink-soft)]">{t('payment.pleaseWaitConfirmation')}</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-[var(--color-surface)] rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-[var(--color-bad-bg)] rounded-full flex items-center justify-center mx-auto mb-6">
            <ExternalLink className="w-8 h-8 text-[var(--color-bad)]" />
          </div>
          <h1 className="text-2xl font-semibold text-[var(--color-ink)] mb-4">
            {t('payment.confirmationFailed')}
          </h1>
          <p className="text-[var(--color-ink-soft)] mb-6">{t('payment.confirmationError')}</p>
          <div className="flex flex-col gap-3">
            <Btn kind="primary" onClick={() => navigate('/formation')}>
              {t('buttons.backToFormation')}
            </Btn>
            <Btn
              kind="ghost"
              icon={<ArrowLeft className="w-4 h-4" />}
              onClick={() => navigate('/')}
            >
              {t('buttons.backToHome')}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="bg-[var(--color-surface)] rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-[var(--color-good-bg)] rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-[var(--color-good)]" />
        </div>

        <h1 className="text-2xl font-semibold text-[var(--color-ink)] mb-4">
          {t('payment.paymentSuccessful')}
        </h1>

        <p className="text-[var(--color-ink-soft)] mb-6">
          {subcategoryName
            ? t('payment.accessGrantedFor', { course: subcategoryName })
            : t('payment.accessGranted')}
        </p>

        {details && (
          <div className="bg-[var(--color-surface-2)] rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-[var(--color-ink)] mb-2">
              {t('payment.paymentDetails')}
            </h3>
            <div className="space-y-1 text-sm text-[var(--color-ink-soft)]">
              <div className="flex justify-between">
                <span>{t('payment.amount')}:</span>
                <span>
                  {details.amount} {details.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t('payment.transactionId')}:</span>
                <span className="font-mono text-xs truncate max-w-[200px]">
                  {details.transactionId}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t('payment.status')}:</span>
                <span className="text-[var(--color-good)] font-medium">
                  {t('payment.completed')}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Btn kind="primary" onClick={() => navigate('/formation')}>
            {t('buttons.startLearning')}
          </Btn>
          <Btn kind="ghost" icon={<ArrowLeft className="w-4 h-4" />} onClick={() => navigate('/')}>
            {t('buttons.backToHome')}
          </Btn>
        </div>
      </div>
    </div>
  );
};
